import { contractFocusOrder, nextEditKey } from '../focus/focusOrder';

import type { ContractEditKey } from './contractStore';
import { useFinancials } from './FinancialsProvider';

import type { CommitIntent } from '@/components/planning/cells';

/**
 * Editing-key plumbing for the Financials inline cells. The cells reuse the
 * generic InlineTextCell; parse helpers live in ./parse (pure, node-testable).
 */
export const useContractEditing = () => {
  const { state, dispatch, budgetModel } = useFinancials();

  const isEditing = (key: ContractEditKey): boolean => state.editingKey === key;
  const startEditing = (key: ContractEditKey) => dispatch({ type: 'START_EDITING', key });
  const stopEditing = () => dispatch({ type: 'STOP_EDITING' });

  /**
   * The v8 keyboard path: after a commit, Enter/Tab moves editing to the next
   * headline field (Shift-Tab the previous); a plain blur commit stays put.
   */
  const advanceFrom = (key: ContractEditKey, intent: CommitIntent) => {
    if (intent !== 'enter-next' && intent !== 'tab-next' && intent !== 'tab-prev') {
      stopEditing();
      return;
    }
    const order = contractFocusOrder(state.document, { config: budgetModel.config });
    const next = nextEditKey(order, key, intent === 'tab-prev' ? -1 : 1);
    if (next === null) stopEditing();
    else startEditing(next);
  };

  return { editingKey: state.editingKey, isEditing, startEditing, stopEditing, advanceFrom };
};
